//#region Imports
import { DatePipe } from '@angular/common';
import { Component, HostListener, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Constants } from '@app/core/constants/constants';
import { BidActionType, LoadCarrierBidStage, LoadCarrierBidStatus } from '@app/core/constants/enums';
import { FormHelper } from '@app/core/helpers/form.helper';
import { TruckMatchResultModel } from '@app/modules/load/load-edit/truck-match/truck-match.model';
import { LoadCarrierBidNotificationAlertModel } from '@app/modules/load/load.model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { OverlayPanel } from 'primeng/overlaypanel';
//#endregion

@Component({
  selector: 'app-load-offer-alert',
  templateUrl: './load-offer-alert.component.html',
  styles: [
  ]
})
export class LoadOfferAlertComponent {

  //#region Variables
  loadCarrierBidNotificationAlertModel!: LoadCarrierBidNotificationAlertModel;
  bid!: any;
  @ViewChild('approveBidPannel') approveBidPannel!: OverlayPanel;
  @ViewChild('rejectBidPannel') rejectBidPannel!: OverlayPanel;
  @ViewChild('counterBidPannel') counterBidPannel!: OverlayPanel;
  bidStages = LoadCarrierBidStage;
  loadBidStatus = LoadCarrierBidStatus;
  bidActionType = BidActionType;
  //#endregion

  //#region Constructor
  /**
   *
   */
  constructor(
    public dynamicDialogService: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    public datePipe: DatePipe,
    private readonly router: Router
  ) {
  }

  //#endregion

  //#region NG Methods
  ngOnInit(): void {
    this.getOffersData();
  }
  //#endregion

  //#region Methods
  /**
   * Get load carrier bid notification alert data.
   */
  get loadCarrierBidNotificationAlertData(): LoadCarrierBidNotificationAlertModel {
    return this.dynamicDialogService.data?.loadCarrierBidNotificationAlertModel;
  }

  /**
   * Set load carrier bid notification alert data.
   */
  getOffersData(): void {
    this.loadCarrierBidNotificationAlertModel = this.loadCarrierBidNotificationAlertData;
    this.bid = {
      loadCarrierBidId: this.loadCarrierBidNotificationAlertModel.loadCarrierBidId,
      bidAmount: this.loadCarrierBidNotificationAlertModel.bidAmount,
      bidStage: this.loadCarrierBidNotificationAlertModel.bidStage,
      loadDetailId: this.loadCarrierBidNotificationAlertModel.loadDetailId
    }
  }

  /**
   * Window Scroll event Listener.
   * @param event 
   */
  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event: any): void {
    if (this.approveBidPannel) {
      this.approveBidPannel.hide();
    }
    if (this.rejectBidPannel) {
      this.rejectBidPannel.hide();
    }
    if (this.counterBidPannel) {
      this.counterBidPannel.hide();
    }
  }

  /**
     * Format Date Time data
     * @returns Formatted Date Time
     */
  dateTimeCellRenderer(): string {
    return this.datePipe?.transform(FormHelper.combineDateAndTime(this.loadCarrierBidNotificationAlertModel?.appointmentEarliestDate, this.loadCarrierBidNotificationAlertModel.appointmentEarliestTime), Constants.dateTimeFormat) ?? '';
  }

  /**
   * On Cover Click
   */
  onCoverClick() {
    const truckMatchResultModel: TruckMatchResultModel = {
      truckMatchResultId: 0,
      mcNumber: this.loadCarrierBidNotificationAlertModel.mcNumber,
      carrierName: this.loadCarrierBidNotificationAlertModel.carrierName,
      carrierId: this.loadCarrierBidNotificationAlertModel.carrierId,
      carrierStatusId: 0,
      carrierStatusName: '',
      status: this.loadCarrierBidNotificationAlertModel.loadCarrierBidStatusName,
      originCity: this.loadCarrierBidNotificationAlertModel.originCity,
      originState: this.loadCarrierBidNotificationAlertModel.originState,
      destinationCity: this.loadCarrierBidNotificationAlertModel.destinationCity,
      destinationState: this.loadCarrierBidNotificationAlertModel.destinationState,
      availableDate: new Date(this.loadCarrierBidNotificationAlertModel.appointmentEarliestDate),
      deadHeadMiles: 0,
      kind: '',
      isActive: true,
      capacity: 0,
      carrierContactName: '',
      carrierContactEmail: '',
      carrierContactPhone: '',
      description: '',
      loadCarrierBidId: this.loadCarrierBidNotificationAlertModel.loadCarrierBidId,
      approvedAmount: this.loadCarrierBidNotificationAlertModel.approvedAmount,
      emptyDate: this.loadCarrierBidNotificationAlertModel.emptyDate,
      emptyTime: this.loadCarrierBidNotificationAlertModel.emptyTime,
      bidStage: this.loadCarrierBidNotificationAlertModel.bidStage,
      createdBy: this.loadCarrierBidNotificationAlertModel.loadCarrierBidCreatedBy,
      loadCarrierBidStatusName: this.loadCarrierBidNotificationAlertModel.loadCarrierBidStatusName
    };
    localStorage.setItem(Constants.offersAssignCarrier, JSON.stringify(truckMatchResultModel));
    this.router.navigate([]).then(() => { window.open(`load/edit/${this.loadCarrierBidNotificationAlertModel.loadDetailId}`, '_blank'); });
    this.ref.close();
  }

  /**
   * Get Lane Details
   * @returns 
   */
  getLane(): string {
    const originCity = this.loadCarrierBidNotificationAlertModel?.originCity || '';
    const originState = this.loadCarrierBidNotificationAlertModel?.originState || '';
    const destinationCity = this.loadCarrierBidNotificationAlertModel?.destinationCity || '';
    const destinationState = this.loadCarrierBidNotificationAlertModel?.destinationState || '';

    return `${originCity}, ${originState} - ${destinationCity}, ${destinationState}`;
  }

  /**
   * On Load Detail Click
   */
  onLoadDetailClick(): void {
    sessionStorage.setItem(Constants.loadProfileActiveTab, '5');
    this.router.navigate([]).then(() => { window.open(`load/edit/${this.loadCarrierBidNotificationAlertModel.loadDetailId}`, '_blank'); });
    this.ref.close();
  }

  onOverlayPanelClose(event: any) {
    if (this.approveBidPannel) {
      this.approveBidPannel.hide();
    }
    if (this.rejectBidPannel) {
      this.rejectBidPannel.hide();
    }
    if (this.counterBidPannel) {
      this.counterBidPannel.hide();
    }
  }
  //#endregion
}
